import { useEffect, useLayoutEffect, useRef, useState } from 'react'

// Grows a controlled <textarea> to fit its content between minHeight and
// maxHeight, then scrolls once past the cap. As soon as the user drags the
// native resize handle, auto-grow steps aside so it never overrides the
// manually chosen height. Returns a callback ref to attach to the textarea
// (re-initialises correctly when the node mounts/remounts, e.g. preview toggle).
export const useAutoGrowTextarea = (
  value: unknown,
  minHeight: number,
  maxHeight: number
) => {
  const [node, setNode] = useState<HTMLTextAreaElement | null>(null)
  const lastSetHeight = useRef(0)
  const userResized = useRef(false)

  useLayoutEffect(() => {
    if (!node || userResized.current) return
    node.style.height = 'auto'
    // border-box target = content + padding (scrollHeight) + border.
    const fitHeight = node.scrollHeight + node.offsetHeight - node.clientHeight
    const height = Math.min(Math.max(fitHeight, minHeight), maxHeight)
    node.style.height = `${height}px`
    node.style.overflowY = fitHeight > maxHeight ? 'auto' : 'hidden'
    lastSetHeight.current = height
  }, [node, value, minHeight, maxHeight])

  useEffect(() => {
    if (!node || typeof ResizeObserver === 'undefined') return
    userResized.current = false
    const observer = new ResizeObserver(() => {
      // A height we did not set ourselves means the user dragged the handle.
      if (Math.abs(node.offsetHeight - lastSetHeight.current) > 1) {
        userResized.current = true
        node.style.overflowY = 'auto'
      }
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [node])

  return setNode
}
